import { useEffect, useRef, useState } from "react";
import { ApiError, transcribeIntakeAudio, extractIntakeText } from "../../api";
import type { ExtractionResult } from "../../types/intake";

interface Props {
  onExtracted: (r: ExtractionResult) => void;
  onCancel: () => void;
  onDirty?: () => void;
  noteVariant?: "intake" | "call";
  /** When true (host has no dismiss guardrail, e.g. the enrich tab), CANCEL
   *  during an active recording asks to confirm before discarding the audio. */
  confirmOnDiscard?: boolean;
}

type Status = "arming" | "armed" | "blocked" | "recording" | "processing" | "error";

/** Rotate to a fresh standalone segment every ~9 min OR ~22 MB, whichever first.
 *  Each segment is ONE complete MediaRecorder session (header + data + closing
 *  atom) → an independently-decodable file (verified on iOS Safari 2026-06-03),
 *  so no upload nears Whisper's 25 MB limit regardless of bitrate handling. */
const SEGMENT_MS = 9 * 60 * 1000;
const SEGMENT_BYTES = 22 * 1024 * 1024;

function pickMime(): string {
  const cands = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/aac"];
  for (const c of cands) {
    try {
      if (MediaRecorder.isTypeSupported(c)) return c;
    } catch {
      /* ignore */
    }
  }
  return ""; // let the browser choose
}

function fmtTime(s: number): string {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}
function fmtMB(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
function extFor(mime: string): string {
  return /mp4|aac|m4a/.test(mime) ? "m4a" : /ogg/.test(mime) ? "ogg" : "webm";
}

/**
 * VoiceCapture — acoustic recording (phone as a 2nd device on a speakerphone
 * call). Mic permission is obtained on open (no mid-call prompt); an explicit
 * START begins recording. Long calls are split into standalone segments — each
 * its OWN MediaRecorder session with its OWN chunk buffer, started only inside
 * the prior session's onstop (serialized) so every segment is a complete,
 * decodable file on iOS Safari. Segments transcribe in order and concatenate.
 * No retention: audio dropped after transcription, transcript after extraction.
 */
export function VoiceCapture({
  onExtracted,
  onCancel,
  onDirty,
  noteVariant = "intake",
  confirmOnDiscard,
}: Props) {
  const [status, setStatus] = useState<Status>("arming");
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [bytes, setBytes] = useState(0);
  const [segCount, setSegCount] = useState(0);
  const [progress, setProgress] = useState("");
  const [error, setError] = useState<string | null>(null);

  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const reqMimeRef = useRef("");
  const actualMimeRef = useRef("audio/webm");
  const segmentsRef = useRef<Blob[]>([]);
  const segStartRef = useRef(0);
  const recordingRef = useRef(false);
  const tickRef = useRef<number | null>(null);
  // Coordinates the async rotation/stop boundary: the next segment (or the
  // stop-resolve) runs INSIDE the stopping recorder's onstop.
  const nextActionRef = useRef<"rotate" | "stop" | null>(null);
  const stopResolveRef = useRef<(() => void) | null>(null);

  async function arm() {
    setStatus("arming");
    setError(null);
    try {
      streamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
      reqMimeRef.current = pickMime();
      setStatus("armed");
    } catch {
      setStatus("blocked");
      setError("Microphone blocked. Allow mic access, then tap “Enable microphone.”");
    }
  }

  useEffect(() => {
    void arm();
    return () => cleanup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function cleanup() {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    recordingRef.current = false;
    nextActionRef.current = null;
    try {
      if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
    } catch {
      /* ignore */
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    segmentsRef.current = [];
  }

  function safeStop(rec: MediaRecorder | null) {
    try {
      if (rec && rec.state !== "inactive") rec.stop();
    } catch {
      /* ignore */
    }
  }

  /** Open a new recording session with its OWN chunk buffer. */
  function startSegment() {
    const stream = streamRef.current;
    if (!stream) return;
    const chunks: BlobPart[] = []; // THIS session's chunks only (closure)
    let segBytes = 0;

    let rec: MediaRecorder;
    try {
      const opts: MediaRecorderOptions = { audioBitsPerSecond: 24000 };
      if (reqMimeRef.current) opts.mimeType = reqMimeRef.current;
      rec = new MediaRecorder(stream, opts);
    } catch {
      rec = new MediaRecorder(stream); // fall back if opts unsupported
    }
    if (rec.mimeType) actualMimeRef.current = rec.mimeType;

    rec.ondataavailable = (e) => {
      if (e.data && e.data.size) {
        chunks.push(e.data);
        segBytes += e.data.size;
        setBytes((b) => b + e.data.size);
        if (segBytes >= SEGMENT_BYTES && nextActionRef.current === null) {
          nextActionRef.current = "rotate"; // size backstop
          safeStop(rec);
        }
      }
    };
    rec.onstop = () => {
      if (chunks.length) {
        segmentsRef.current.push(new Blob(chunks, { type: rec.mimeType || actualMimeRef.current }));
        setSegCount(segmentsRef.current.length);
      }
      const action = nextActionRef.current;
      nextActionRef.current = null;
      if (action === "rotate" && recordingRef.current) {
        startSegment(); // next session opens only after this one fully closed
      } else if (action === "stop") {
        stopResolveRef.current?.();
        stopResolveRef.current = null;
      }
    };

    segStartRef.current = Date.now();
    try {
      rec.start(1000); // 1s timeslice → live size; full session still concatenated
    } catch {
      rec.start();
    }
    recorderRef.current = rec;
  }

  function start() {
    if (status !== "armed") return;
    onDirty?.();
    segmentsRef.current = [];
    setSegCount(0);
    setBytes(0);
    setElapsed(0);
    recordingRef.current = true;
    nextActionRef.current = null;
    setStatus("recording");
    startSegment();
    tickRef.current = window.setInterval(() => {
      setElapsed((s) => s + 1);
      if (Date.now() - segStartRef.current >= SEGMENT_MS && nextActionRef.current === null) {
        nextActionRef.current = "rotate";
        safeStop(recorderRef.current);
      }
    }, 1000);
  }

  async function stop() {
    recordingRef.current = false;
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    const rec = recorderRef.current;
    if (rec && rec.state !== "inactive") {
      await new Promise<void>((resolve) => {
        stopResolveRef.current = resolve;
        nextActionRef.current = "stop";
        safeStop(rec);
      });
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    setStatus("processing");
    await transcribeAndExtract();
  }

  async function transcribeAndExtract() {
    const segs = segmentsRef.current;
    if (segs.length === 0) {
      setError("No audio captured.");
      setStatus("error");
      return;
    }
    const ext = extFor(actualMimeRef.current);
    const transcripts: string[] = [];
    try {
      for (let i = 0; i < segs.length; i++) {
        setProgress(`Transcribing ${i + 1} of ${segs.length}…`);
        try {
          transcripts.push(await transcribeIntakeAudio(segs[i], `segment-${i + 1}.${ext}`));
        } catch {
          transcripts.push(`[segment ${i + 1} could not be transcribed]`); // never silently drop
        }
      }
      segmentsRef.current = []; // discard audio
      setProgress("Extracting…");
      const result = await extractIntakeText(transcripts.join("\n"), "audio", noteVariant);
      onExtracted(result);
    } catch (err) {
      setError(err instanceof ApiError ? `${err.code} (HTTP ${err.status})` : "extract_failed");
      setStatus("error");
    }
  }

  return (
    <div className="intake-voice">
      {status === "arming" && (
        <div className="intake-voice-status">
          <span className="chat-thinking-dot" />
          <span className="chat-thinking-dot" />
          <span className="chat-thinking-dot" /> enabling microphone…
        </div>
      )}

      {status === "blocked" && (
        <div className="intake-voice-armrow">
          <div className="write-confirm-error">⚠ {error}</div>
          <button className="intake-voice-start" onClick={() => void arm()}>
            ENABLE MICROPHONE
          </button>
        </div>
      )}

      {status === "armed" && (
        <div className="intake-voice-armrow">
          <div className="intake-voice-ready">🎙 Mic ready</div>
          <div className="intake-voice-hint">
            Arm before the call connects; tap start once the borrower's on the line. Long calls
            auto-split so nothing truncates.
          </div>
          <button className="intake-voice-start" onClick={start}>
            ● START RECORDING
          </button>
        </div>
      )}

      {status === "recording" && (
        <div className="intake-voice-recording">
          <div className="intake-voice-meter">
            <span className="intake-voice-rec-dot" />
            <span className="intake-voice-timer">{fmtTime(elapsed)}</span>
            <span className="intake-voice-size">
              {fmtMB(bytes)} · {segCount + 1} segment{segCount + 1 === 1 ? "" : "s"}
            </span>
          </div>
          <button className="intake-voice-stop" onClick={() => void stop()}>
            ■ STOP & TRANSCRIBE
          </button>
        </div>
      )}

      {status === "processing" && (
        <div className="intake-voice-status">
          <span className="chat-thinking-dot" />
          <span className="chat-thinking-dot" />
          <span className="chat-thinking-dot" /> {progress || "processing…"}
        </div>
      )}

      {status === "error" && error && <div className="write-confirm-error">⚠ {error}</div>}

      <div className="intake-actions">
        <button
          className="write-confirm-cancel"
          onClick={() => {
            if (confirmOnDiscard && status === "recording") {
              setConfirmingDiscard(true);
            } else {
              cleanup();
              onCancel();
            }
          }}
          disabled={status === "processing"}
        >
          CANCEL
        </button>
      </div>

      {confirmingDiscard && (
        <div className="intake-discard-overlay">
          <div className="intake-discard">
            <div className="intake-discard-msg">
              Discard the recording in progress? The audio will be lost.
            </div>
            <div className="intake-actions">
              <button className="write-confirm-cancel" onClick={() => setConfirmingDiscard(false)}>
                KEEP RECORDING
              </button>
              <button
                className="write-confirm-confirm"
                onClick={() => {
                  setConfirmingDiscard(false);
                  cleanup();
                  onCancel();
                }}
              >
                DISCARD
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
